import axios from 'axios';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react'
import { Modal, Button } from 'react-bootstrap';
import Select from 'react-select';
import DateRangePicker from 'react-bootstrap-daterangepicker';
import { useUser } from '../../utils/userContext';
import { toast, ToastContainer } from 'react-toastify';
import useHandleErrors from '../../utils/user.utils';
import { utils, write } from 'xlsx';
import { saveAs } from 'file-saver';


const LeaveApproval = () => {
    const [leaves, setLeaves] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [selectedLeave, setSelectedLeave] = useState(null);
    const [formData, setFormData] = useState({});
    const [action, setAction] = useState('');
    const [comment, setComment] = useState('');
    const { user } = useUser();
    const [loading, setLoading] = useState(false);
    const handleErrors = useHandleErrors();
    const token = localStorage.getItem("token");
    const [filteredData, setFilteredData] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [recordsPerPage, setRecordsPerPage] = useState(10);
    const hasFetchedRef = useRef(false);

    const getLeaveStatus = async () => {
        setLoading(true)
        if (user && user.OfficialEmail) {
            try {
                const path = `/LeaveTracker/list?emailId=${user.OfficialEmail}`;
                const URL = process.env.REACT_APP_BE_URL + path;
                const response = await axios.get(URL, {
                    headers: {
                        Authorization: `Bearer ${token}`
                    },
                });
                if (response.status === 200) {
                    const responseData = response.data.leaveRequestsWithMyApproval || [];
                    setLeaves(responseData);
                    setFilteredData(responseData);
                }
                else {
                    handleErrors(response);
                }
            } catch (error) {
                if (error.response) {
                    if (error.response.status === 404) {
                        // Handle 404 status code separately
                        setLeaves([]);
                        setFilteredData([]);
                    } else {
                        handleErrors(error.response);
                    }
                } else {
                    toast.error("Something went wrong. Please try again later.", {
                        position: toast.POSITION.TOP_CENTER,
                    });
                }
            } finally {
                setLoading(false);
            }
        }
    };

    useEffect(() => {
        if (!hasFetchedRef.current) {
            getLeaveStatus();
            hasFetchedRef.current = true;
        }
    }, [user]);


    const handleModalClose = () => setShowModal(false);
    const handleModalShow = (leave) => {
        setSelectedLeave(leave);
        setFormData({
            userEmailId: leave.employeeEmail,
            leaveType: leave.leaveType,
            startDate: leave.startDate,
            endDate: leave.endDate,
            daysOfWeek: leave.daysOfWeek,
            description: leave.description,
            alternateContactPerson: leave.alternateContactPerson,
            managerEmail: leave.managerEmail
        });
        setShowModal(true);
    };

    const handleApproveOrReject = async (LeaveId, EmailId, Status) => {
        const payload = {
            LeaveId,
            Status,
            EmailId,
            Comments: comment
        };
        try {
            const path = `/LeaveTracker/action`;
            const URL = process.env.REACT_APP_BE_URL + path;
            const response = await axios.put(URL, payload, {
                headers: {
                    Authorization: `Bearer ${token}`
                },
            })
            if (response.status === 200) {
                console.log('Action response:', response.data);
                toast.success(`Leave ${Status} Successfully`, {
                    position: toast.POSITION.TOP_CENTER,
                });
                handleModalClose();
                getLeaveStatus();
            }
            else {
                handleErrors(response)
            }
        } catch (error) {
            if (error.response) {
                handleErrors(error.response);
            } else {
                toast.error("Something went wrong. Please try again later.", {
                    position: toast.POSITION.TOP_CENTER,
                });
            }
        }
    };

    const handleSubmit = () => {
        handleApproveOrReject(selectedLeave.leaveId, user?.OfficialEmail, action);
    };

    const handleEvent = (event, picker) => {
        setFormData({
            ...formData,
            startDate: picker.startDate.format('YYYY-MM-DD'),
            endDate: picker.endDate.format('YYYY-MM-DD')
        });
    };

    const sendEmail = () => {
        const ws = utils.json_to_sheet(filteredData);
        const wb = utils.book_new();
        utils.book_append_sheet(wb, ws, 'Leaves');
        const excelBuffer = write(wb, { bookType: 'xlsx', type: 'array' });

        const data = new Blob([excelBuffer], { type: 'application/octet-stream' });
        const today = new Date();
        const year = today.getFullYear().toString().slice(-2); // Get last two digits of the year
        const month = (today.getMonth() + 1).toString().padStart(2, '0'); // Add leading 0 for month
        const day = today.getDate().toString().padStart(2, '0'); // Add leading 0 for day
        const formattedDate = `${year}${month}${day}`;
        
        // Download the Excel file
        saveAs(data, `WW360-Leave_Report_${formattedDate}.xlsx`);

        // Open the email client
        const mailtoLink = `mailto:?subject=Assigned Asset List&body=Please find the attached file.`;
        toast.info('The Excel file has been downloaded. Please attach it to your email.', {
            position: toast.POSITION.TOP_CENTER,
        });

        setTimeout(() => {
            window.location.href = mailtoLink;
        }, 3000);
    };

    const handleExport = () => {
        const ws = utils.json_to_sheet(filteredData);
        const wb = utils.book_new();
        utils.book_append_sheet(wb, ws, 'Assets');
        const excelBuffer = write(wb, { bookType: 'xlsx', type: 'array' });
        const data = new Blob([excelBuffer], { type: 'application/octet-stream' });
        const today = new Date();
        const year = today.getFullYear().toString().slice(-2); // Get last two digits of the year
        const month = (today.getMonth() + 1).toString().padStart(2, '0'); // Add leading 0 for month
        const day = today.getDate().toString().padStart(2, '0'); // Add leading 0 for day
        const formattedDate = `${year}${month}${day}`;
        saveAs(data, `WW360-Leave_Report_${formattedDate}.xlsx`);
    };

    const handleShare = async () => {
        try {
            await navigator.share({
                title: 'Leave List',
                text: 'Check out the Leave list',
                url: window.location.href
            });
        } catch (err) {
            console.error('Error sharing:', err);
        }
    };

    useEffect(() => {
        setFilteredData(
            leaves?.filter(data =>
                data.leaveType.toLowerCase().includes(searchQuery.toLowerCase()) ||
                data.status.toLowerCase().includes(searchQuery.toLowerCase())||
                data.employeeEmail.toLowerCase().includes(searchQuery.toLowerCase())
            )
        );
    }, [searchQuery, leaves]);

    const handleRecordsPerPageChange = (event) => {
        setRecordsPerPage(Number(event.target.value));
        setCurrentPage(1);
    };

    const indexOfLastRecord = currentPage * recordsPerPage;
    const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
    const currentRecords = filteredData.slice(indexOfFirstRecord, indexOfLastRecord);
    const totalPages = Math.ceil(filteredData.length / recordsPerPage);

    const paginate = (pageNumber) => setCurrentPage(pageNumber);


    return (
        <div>
            {loading && (
                <div id="preloading">
                    <span className="loader"></span>
                </div>
            )}
            <ToastContainer />
            <div class="content-page">
                <div class="container-fluid">
                    <div class="row mb-2">
                        <div class="col">
                            <h3 class="title mb-2">Leave Approval</h3>
                        </div>
                        <div class="col-auto"><a href={"/SubmitTimesheet"} data-extra-toggle="tab" data-target="#timesheet-pan" class="btn btn-primary mt-lg-0 mt-4"><i class="ri-calendar-schedule-fill"></i> Timesheet</a></div>
                    </div>
                    {leaves?.length === 0 ? (
                        <div class="container">
                            <div class="row vh-50 justify-content-center align-items-center">
                                <div class="col-auto">
                                    <div class="no-data-wrapper">
                                        <div class="no-data-found">
                                            <i class="ri-file-damage-fill"></i>
                                            <div class="bounce"><i class="ri-search-eye-line"></i></div>
                                            <p>No Data Found</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div>
                            <div class="row align-items-center justify-content-center">
                                <div class="col-5">
                                    <div class="container-fluid row-rounded mb-0">
                                        <div class="row px-2 py-0">
                                            <div class="input-group mb-0">
                                                <input
                                                    type="search"
                                                    className="form-control search-input"
                                                    placeholder="Keyword search..."
                                                    value={searchQuery}
                                                    onChange={(e) => setSearchQuery(e.target.value)}
                                                />                                                        <div class="input-group-append">
                                                    <button class="btn btn-primary" type="button" title="Search" rel="tooltip"><i class="ri-menu-search-line"></i></button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <small class="form-text text-muted text-center mt-0"><i class="ri-information-2-fill text-info"></i> Allows: Leave Type - Status </small>
                                </div>
                            </div>
                            <div class="row">
                                <div class="col-lg-12">
                                    <div class="card mb-4">
                                        <div class="card-body">
                                            <div class="row">
                                                <div class="col">
                                                    <h5 class="title mt-1 mb-0">Approval Pending</h5>
                                                </div>
                                                <div className="col-auto">
                                                    <button type="button" title="Email" className="btn btn-light btn-circle ml-2" onClick={() => sendEmail('')}>
                                                        <i className="ri-mail-send-fill"></i>
                                                    </button>
                                                    <button type="button" title="Share" className="btn btn-light btn-circle ml-2" onClick={handleShare}>
                                                        <i className="ri-share-fill"></i>
                                                    </button>
                                                    <button type="button" title="Export" className="btn btn-light btn-circle ml-2" onClick={handleExport}>
                                                        <i className="ri-export-fill"></i>
                                                    </button>
                                                </div>
                                            </div>
                                            <div class="row mt-3">
                                                <div className="col-md-12">
                                                    <div className="table-responsive leave-information">
                                                        <table className="table grid">
                                                            <thead className="bg-light">
                                                                <tr>
                                                                    <th>S.No.</th>
                                                                    <th>Employee Email</th>
                                                                    <th>Leave Type</th>
                                                                    <th>Start Date</th>
                                                                    <th>End Date</th>
                                                                    <th>Leave Balance</th>
                                                                    <th>Status</th>
                                                                    <th></th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {currentRecords?.map((leave, index) => (
                                                                    <tr key={leave.leaveId}>
                                                                        <td>{index + 1 + indexOfFirstRecord}</td>
                                                                        <td>{leave.employeeEmail}</td>
                                                                        <td>{leave.leaveType}</td>
                                                                        <td>{moment(leave.startDate).format("DD-MM-YYYY")}</td>
                                                                        <td>{moment(leave.endDate).format("DD-MM-YYYY")}</td>
                                                                        <td className="text-center"><span className="text-info border badge border-info">{leave.leaveBalance}</span></td>
                                                                        <td>
                                                                            <span className={`badge border text-${leave.status === 'Pending' ? 'warning' :
                                                                                leave.status === 'Approved' ? 'success' :
                                                                                    leave.status === 'Submitted' ? 'secondary' : 'danger'
                                                                                } status`}>
                                                                                <i className={`ri-${leave.status === 'Pending' ? 'hourglass-fill' :
                                                                                    leave.status === 'Approved' ? 'thumb-up-fill' :
                                                                                        leave.status === 'Submitted' ? 'thumb-up-fill' : 'thumb-down-fill'
                                                                                    }`}></i> {leave.status}
                                                                            </span>
                                                                        </td>

                                                                        <td className="text-center">
                                                                            <button className="btn btn-link text-info btn-action custom-dropdown-btn" type="button" title="Update" onClick={() => handleModalShow(leave)}>
                                                                                <i className="ri-list-settings-line"></i>
                                                                            </button>
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>

                                                <div className="col-md-12">
                                                    <div className="container-fluid">
                                                        <div className="row border-top pt-3">
                                                            <div className="col-auto">
                                                                <p className="mt-1 mb-0">Records</p>
                                                            </div>
                                                            <div className="col-1 record-drop">
                                                                <select className="form-control" value={recordsPerPage} onChange={handleRecordsPerPageChange}>
                                                                    <option value="5">5</option>
                                                                    <option value="10">10</option>
                                                                    <option value="15">15</option>
                                                                    <option value="20">20</option>
                                                                    <option value="25">25</option>
                                                                </select>
                                                            </div>
                                                            <div className="col">
                                                                <nav>
                                                                    <ul className="pagination justify-content-center">
                                                                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                                                            <button className="page-link" onClick={() => paginate(1)} tabIndex="-1">
                                                                                <i className="ri-arrow-left-double-line"></i>
                                                                            </button>
                                                                        </li>
                                                                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                                                            <button className="page-link" onClick={() => paginate(currentPage - 1)} tabIndex="-1">
                                                                                <i className="ri-arrow-left-s-line"></i>
                                                                            </button>
                                                                        </li>
                                                                        {Array.from({ length: totalPages }, (_, index) => (
                                                                            <li key={index + 1} className={`page-item ${index + 1 === currentPage ? 'active' : ''}`}>
                                                                                <button className="page-link" onClick={() => paginate(index + 1)}>
                                                                                    {index + 1}
                                                                                </button>
                                                                            </li>
                                                                        ))}
                                                                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                                                            <button className="page-link" onClick={() => paginate(currentPage + 1)}>
                                                                                <i className="ri-arrow-right-s-line"></i>
                                                                            </button>
                                                                        </li>
                                                                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                                                            <button className="page-link" onClick={() => paginate(totalPages)}>
                                                                                <i className="ri-arrow-right-double-fill"></i>
                                                                            </button>
                                                                        </li>
                                                                    </ul>
                                                                </nav>
                                                            </div>
                                                            <div className="col-auto">
                                                                <p className="mt-1 mb-0">Page Showing {currentPage} of {totalPages}</p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                </div>
            </div>

            {selectedLeave && (
                <Modal show={showModal} onHide={handleModalClose} size="lg" centered>
                    <Modal.Header closeButton>
                        <Modal.Title>Update Leave</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <h5 className="title">
                            <small>Employee Email - <span>{selectedLeave.employeeEmail}</span></small>
                        </h5>
                        <hr />
                        <div className="row">
                            <div className="col-lg-6 col-md-6">
                                <div className="form-group">
                                    <label htmlFor="leaveType" className="required">Leave Type</label>
                                    <Select
                                        className="react-select-container"
                                        classNamePrefix="react-select"
                                        name="leaveType"
                                        value={{ label: formData.leaveType, value: formData.leaveType }}
                                        options={[{ label: formData.leaveType, value: formData.leaveType }]}
                                        isDisabled
                                    />
                                </div>
                            </div>
                            <div className="col-lg-6 col-md-6">
                                <div className="form-group">
                                    <label htmlFor="dateRange" className="required">Date Range</label>
                                    <div className="input-group dateRange">
                                        <DateRangePicker
                                            initialSettings={{
                                                startDate: moment(formData.startDate, 'YYYY-MM-DD').toDate(),
                                                endDate: moment(formData.endDate, 'YYYY-MM-DD').toDate(),
                                                locale: {
                                                    format: 'YYYY-MM-DD',
                                                },
                                            }}
                                            onEvent={handleEvent}
                                        >
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="dateRange"
                                                placeholder="Select Date Range"
                                                value={`${moment(formData.startDate).format('DD-MM-YYYY')} - ${moment(formData.endDate).format('DD-MM-YYYY')}`}
                                                disabled
                                            />
                                        </DateRangePicker>
                                        <div className="input-group-append">
                                            <span className="input-group-text bg-white">
                                                <i className="ri-calendar-2-line"></i>
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="col-lg-12 col-md-12">
                                <div className="form-group">
                                    <label>Days of Week</label>
                                    <div className="d-flex" style={{ flexWrap: 'nowrap', overflowX: 'auto' }}>
                                                    {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map((day) => (
                                                        <div
                                                            key={day}
                                                            className="p-2"
                                                            style={{
                                                                flex: '0 0 calc(100% / 7)', // Each item takes 1/7th of the row
                                                                boxSizing: 'border-box'
                                                            }}
                                                        >

                                                <div className="custom-control custom-checkbox">
                                                    <input
                                                        type="checkbox"
                                                        className="custom-control-input"
                                                        name={day}
                                                        id={day}
                                                        checked={selectedLeave.recurrence?.includes(day)}
                                                        disabled
                                                    />
                                                    <label className="custom-control-label" htmlFor={day}>{day}</label>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-lg-12 col-md-12">
                                <div className="form-group">
                                    <label htmlFor="description">Description</label>
                                    <textarea className="form-control" rows="5" name="description" value={selectedLeave.description} disabled></textarea>
                                </div>
                            </div>
                            <div className="col-lg-12 col-md-12">
                                <div className="form-group">
                                    <label htmlFor="document">Document <small className="text-muted">(If any)</small></label>
                                    <ul className="list-unstyled">
                                        {selectedLeave?.leaveDocuments.map((file, index) => (
                                            <li key={index}>
                                                <a href={file} target="_blank" rel="noopener noreferrer">
                                                    {file.split('?')[0].split('/').pop()}
                                                </a>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                            <div className="col-lg-6 col-md-6">
                                <div className="form-group">
                                    <label htmlFor="managerEmail" className="required">Alternate Contact Person</label>
                                    <input
                                        className="form-control"
                                        type="email"
                                        name="alternateContact"
                                        value={selectedLeave.alternateContact || ''}
                                        disabled
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="row">
                            <div className="form-group col-6">
                                <div className="custom-control custom-radio">
                                    <input
                                        type="radio"
                                        className="custom-control-input"
                                        name="updateClaim"
                                        id="approve"
                                        onClick={() => setAction('Approved')}
                                    />
                                    <label className="custom-control-label control-label-1" htmlFor="approve">Approve</label>
                                </div>
                            </div>
                            <div className="form-group col-6">
                                <div className="custom-control custom-radio">
                                    <input
                                        type="radio"
                                        className="custom-control-input"
                                        name="updateClaim"
                                        id="reject"
                                        onClick={() => setAction('Rejected')}
                                    />
                                    <label className="custom-control-label control-label-1" htmlFor="reject">Reject</label>
                                </div>
                            </div>
                        </div>
                        {action === 'Rejected' && (
                            <div className="form-group">
                                <label htmlFor="notifyComment">Comment</label>
                                <textarea
                                    className="form-control"
                                    rows="3"
                                    id="notifyComment"
                                    value={comment}
                                    onChange={e => setComment(e.target.value)}
                                ></textarea>
                            </div>
                        )}
                    </Modal.Body>
                    <Modal.Footer className="justify-content-center py-2">
                        <Button variant="light" onClick={handleModalClose}>Cancel</Button>
                        <Button variant="primary" onClick={handleSubmit}>Submit <i className="ri-checkbox-circle-line"></i></Button>
                    </Modal.Footer>
                </Modal>
            )}
        </div>
    )
}

export default LeaveApproval
